/** Shapes returned by the developer API (apps/api/src/routes/developer.ts). */
export type Role = 'owner' | 'admin' | 'developer' | 'read_only';
export const ROLES: Role[] = ['owner', 'admin', 'developer', 'read_only'];
export const ROLE_RANK: Record<Role, number> = { read_only: 0, developer: 1, admin: 2, owner: 3 };
export const ROLE_LABEL: Record<Role, string> = { owner: 'Owner', admin: 'Admin', developer: 'Developer', read_only: 'Read-only' };

export type ServiceEnvironment = 'sandbox' | 'production';
export type ServiceStatus = 'pending_credentials' | 'pending_activation' | 'active' | 'suspended' | 'revoked';

export interface Organization {
  id: string;
  display_name: string;
  created_at: string;
  role: Role;
}

export interface Member {
  memberRef: string;
  role: Role;
  createdAt: string;
  you: boolean;
}

/** A domain the organization has proven (production only). Its services may use it and its subdomains. */
export interface OrgDomain {
  domain: string;
  status: 'pending' | 'verified' | 'failed' | 'stale';
  verifiedAt: string | null;
  lastCheckedAt: string | null;
  challengeExpiresAt: string;
  createdAt: string;
  usedBy: { serviceId: string; name: string; uses: string[] }[];
}

export interface ServiceKey {
  id: string;
  direction: 'A' | 'B';
  algorithm: string;
  public_key: string;
  fingerprint: string;
  status: 'active' | 'revoked';
  created_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
  /** The key generation (secret key) this key belongs to: svc_a_<g> and svc_b_<g> share <g>. */
  generation: string | null;
}

export interface ClaimConfig {
  requested: string[];
  ttlSeconds: number;
  onUnavailable: 'deny' | 'prompt';
  minLevel?: 'low' | 'substantial' | 'high';
}

export interface SecurityConfig {
  requireStepUpForLinking?: boolean;
}

export interface Service {
  id: string;
  organizationId: string;
  displayName: string;
  status: ServiceStatus;
  environment: ServiceEnvironment;
  claimConfig: ClaimConfig;
  securityConfig: SecurityConfig;
  activationPending: boolean;
  /** The organization's verified domain its redirect URIs are on (production only). */
  domain: string | null;
  keys: ServiceKey[];
  redirectUris: string[];
  connections: number;
  /** Pryme's attestation issuer keys (ADR-0014): they verify identity bindings. Not tied to any secret key. */
  issuerKeys: { keyId: string; publicKey: string }[];
  apiAudience: string;
  apiBase: string;
  issuer: string;
  createdAt: string;
  role?: Role;
}

export interface CredentialPanel {
  serviceId: string;
  environment: string;
  /** The single value a backend configures: `sk_test_…` / `sk_live_…` (this generation's signing key + Pryme verification key + API base and audience). */
  secretKey: string;
  apiBase: string;
  apiAudience: string;
  issuer: string;
  prymePublicKey: { keyId: string; publicKey: string; fingerprint: string; use?: string };
  serviceSigningKey: { keyId: string; secretKey: string; publicKey: string; fingerprint: string; use?: string };
  warning: string;
  revoked?: string[];
}

export interface DevEvent {
  id: number;
  kind: string;
  result_code: string;
  detail: string | null;
  occurred_at: string;
}

export interface DomainChallenge {
  domain: string;
  record: { type: string; name: string; value: string };
  expiresAt: string;
  note?: string;
}

export interface MeInfo {
  status: string;
  displayStatus: string;
  environment: string;
  assurance: { environment: string; category: string; level: string; provider: string; scheme: string; verified_at: string } | null;
}

export const STATUS_LABEL: Record<ServiceStatus, string> = {
  pending_credentials: 'Needs credentials',
  pending_activation: 'Awaiting activation',
  active: 'Active',
  suspended: 'Suspended',
  revoked: 'Revoked',
};

export function statusTone(s: ServiceStatus): 'ok' | 'warn' | 'bad' | 'neutral' {
  if (s === 'active') return 'ok';
  if (s === 'suspended' || s === 'revoked') return 'bad';
  return 'warn';
}

export const canAdmin = (role?: Role) => !!role && ROLE_RANK[role] >= ROLE_RANK.admin;
export const canDevelop = (role?: Role) => !!role && ROLE_RANK[role] >= ROLE_RANK.developer;

export const activeKeys = (s: Service, dir?: 'A' | 'B') => s.keys.filter((k) => k.status === 'active' && (!dir || k.direction === dir));
export const verifiedOrgDomains = (domains: OrgDomain[]) => domains.filter((d) => d.status === 'verified').map((d) => d.domain);

/** The verified domain `host` is on: the domain itself or a parent of it (longest match). */
export function coveringDomain(host: string, verified: string[]): string | null {
  const h = host.toLowerCase().replace(/\.$/, '');
  return verified.filter((d) => h === d || h.endsWith(`.${d}`)).sort((a, b) => b.length - a.length)[0] ?? null;
}

export function parseDetail(d: string | null): Record<string, unknown> {
  if (!d) return {};
  try {
    const j = JSON.parse(d);
    return j && typeof j === 'object' ? j : {};
  } catch {
    return {};
  }
}
